import { inject, Injectable, InjectionToken, makeEnvironmentProviders, signal } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { HubConnectionState, LogLevel } from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import { ApiService } from './api.service';

export const CLIENT_ID: InjectionToken<string> = new InjectionToken<string>('CUE_CLIENT_ID');
export const AUTH_TOKEN_GETTER: InjectionToken<() => string> = new InjectionToken<() => string>(
  'CUE_AUTH_TOKEN_GETTER'
);
export const SIGNALR_HUB_PATH: InjectionToken<string> = new InjectionToken<string>('CUE_SIGNALR_HUB_PATH');

export interface ControllableObjectUpdateData {
  objectId: string;
  data: any;
}

export enum SignalRStatusEnum {
  Disconnected = 'Disconnected',
  StoppedByClient = 'StoppedByClient',
  Connecting = 'Connecting',
  Connected = 'Connected',
  Reconnecting = 'Reconnecting',
}

export interface SignalREvent {
  name: string;
  payload: any;
  hubPath: string;
}

interface SignalRListenerRegistration {
  eventName: string;
  signalWithoutMessage: boolean;
  hubPath: string;
}

@Injectable()
export class SignalRService {
  private events$ = new Subject<SignalREvent>();
  private clientId = inject<string>(CLIENT_ID);
  private apiService = inject(ApiService);
  private defaultHubPath = inject(SIGNALR_HUB_PATH, { optional: true }) ?? '/MessageHub';

  private listeners = new Map<string, Observable<any>>();
  private listenerRegistrations = new Map<string, SignalRListenerRegistration>();
  private circuitBreakerCheckingByHub = new Map<string, ReturnType<typeof setInterval>>();
  private hubConnections = new Map<string, signalR.HubConnection>();

  tokenGetter = inject(AUTH_TOKEN_GETTER);
  signalRStatusInfoList = signal<string[]>([]);
  signalRStatus = signal<SignalRStatusEnum>(SignalRStatusEnum.Disconnected);
  signalRConnectionRestarted = signal<Date | null>(null);
  signalRConnectionRestartedHubPath = signal<string | null>(null);

  messageSentViaSignalR = signal<Date | null>(null);

  public get hubConnection(): signalR.HubConnection | undefined {
    return this.getHubConnection(this.defaultHubPath);
  }

  private normalizeHubPath(hubPath?: string): string {
    const path = (hubPath ?? this.defaultHubPath)?.trim();
    return path?.length ? path : '/MessageHub';
  }

  private getListenerKey(eventName: string, hubPath: string): string {
    return `${hubPath.toLowerCase()}::${eventName.toLowerCase()}`;
  }

  private getHubConnection(hubPath: string): signalR.HubConnection | undefined {
    return this.hubConnections.get(hubPath);
  }

  private setHubConnection(hubPath: string, connection: signalR.HubConnection) {
    this.hubConnections.set(hubPath, connection);
  }

  private setupCircuitBreakerCheck(hubPath: string) {
    this.disposeCircuitBreakerCheck(hubPath);

    const intervalId = setInterval(() => {
      const hubConnection = this.getHubConnection(hubPath);
      if (hubConnection && hubConnection.state === HubConnectionState.Disconnected) {
        console.log(`CIRCUIT BREAKER: SignalR is disconnected for ${hubPath}. Restarting connection...`);
        this.restartConnection(hubPath);
      }
    }, 5000);

    this.circuitBreakerCheckingByHub.set(hubPath, intervalId);
  }

  private disposeCircuitBreakerCheck(hubPath: string) {
    const intervalId = this.circuitBreakerCheckingByHub.get(hubPath);
    if (intervalId) {
      clearInterval(intervalId);
      this.circuitBreakerCheckingByHub.delete(hubPath);
    }
  }

  private bindListenerToHub(connection: signalR.HubConnection, registration: SignalRListenerRegistration) {
    const { eventName, signalWithoutMessage, hubPath } = registration;

    connection.off(eventName);

    if (signalWithoutMessage) {
      connection.on(eventName, (objectId) => {
        this.events$.next({
          name: eventName,
          payload: objectId,
          hubPath,
        });
      });
      return;
    }

    connection.on(eventName, (objectId, message) => {
      this.events$.next({
        name: eventName,
        payload: {
          objectId,
          data: JSON.parse(message),
        },
        hubPath,
      });
    });
  }

  private bindRegisteredListenersForHub(connection: signalR.HubConnection, hubPath: string) {
    this.listenerRegistrations.forEach((registration) => {
      if (registration.hubPath === hubPath) {
        this.bindListenerToHub(connection, registration);
      }
    });
  }

  public startConnection = (hubPath?: string) => {
    const selectedHubPath = this.normalizeHubPath(hubPath);
    const url = this.apiService.getUrl(selectedHubPath);
    const existingConnection = this.getHubConnection(selectedHubPath);

    if (existingConnection?.state === HubConnectionState.Connected) {
      this.disposeCircuitBreakerCheck(selectedHubPath);
      return;
    }
    if (
      existingConnection?.state === HubConnectionState.Connecting ||
      existingConnection?.state === HubConnectionState.Reconnecting
    ) {
      return;
    }

    this.signalRStatusInfoList.update((list) => [...list, `Restarting SignalR connection (${selectedHubPath})...`]);

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => {
          const token = this.tokenGetter();
          return token ? token : undefined;
        },
      })
      .withAutomaticReconnect([5000, 10000, 20000, 40000, 10000, 10000, 20000, 100000])
      .configureLogging(LogLevel.None)
      .build();

    this.setHubConnection(selectedHubPath, connection);
    this.bindRegisteredListenersForHub(connection, selectedHubPath);

    connection.onreconnecting(() => {
      this.signalRStatus.set(SignalRStatusEnum.Reconnecting);
    });

    connection.onreconnected(() => {
      this.signalRStatus.set(SignalRStatusEnum.Connected);
      this.disposeCircuitBreakerCheck(selectedHubPath);
    });

    connection.onclose((err) => {
      if (err) {
        this.signalRStatus.set(SignalRStatusEnum.Disconnected);
      } else {
        this.signalRStatus.set(SignalRStatusEnum.StoppedByClient);
      }
      this.setupCircuitBreakerCheck(selectedHubPath);
    });

    connection
      .start()
      .then(() => {
        const message = `[${new Date(Date.now())}] SignalR connection started (${selectedHubPath})`;
        this.signalRStatus.set(SignalRStatusEnum.Connected);
        this.signalRStatusInfoList.update((list) => [...list, message]);
        console.log(message);
      })
      .catch((err) => {
        const message = `[${new Date(Date.now())}] Error while starting SignalR connection (${selectedHubPath}): ${err}`;
        this.signalRStatus.set(SignalRStatusEnum.Disconnected);
        this.signalRStatusInfoList.update((list) => [...list, message]);
        console.log(message);
      });
  };

  public restartConnection(hubPath?: string) {
    const selectedHubPath = this.normalizeHubPath(hubPath);
    const connection = this.getHubConnection(selectedHubPath);

    if (connection?.state === HubConnectionState.Connected) {
      connection.stop().then(() => {
        this.startConnection(selectedHubPath);
        this.signalRConnectionRestarted.set(new Date(Date.now()));
        this.signalRConnectionRestartedHubPath.set(selectedHubPath);
      });
    } else if (connection?.state === HubConnectionState.Disconnected || connection == null) {
      this.startConnection(selectedHubPath);
      this.signalRConnectionRestarted.set(new Date(Date.now()));
      this.signalRConnectionRestartedHubPath.set(selectedHubPath);
    }
  }

  public listen<T = any>(eventName: string, signalWithoutMessage = false, hubPath?: string): Observable<T> {
    const selectedHubPath = this.normalizeHubPath(hubPath);
    const listenerKey = this.getListenerKey(eventName, selectedHubPath);

    const existing = this.listeners.get(listenerKey);
    if (existing) {
      return existing as Observable<T>;
    }

    const registration: SignalRListenerRegistration = {
      eventName,
      signalWithoutMessage,
      hubPath: selectedHubPath,
    };

    this.listenerRegistrations.set(listenerKey, registration);

    const hubConnection = this.getHubConnection(selectedHubPath);
    if (hubConnection) {
      this.bindListenerToHub(hubConnection, registration);
    }

    const observable = new Observable<T>((subscriber) => {
      const sub = this.events$.subscribe((e) => {
        if (
          e.hubPath.toLowerCase() === selectedHubPath.toLowerCase() &&
          e.name.toLowerCase() === eventName.toLowerCase()
        ) {
          subscriber.next(e.payload);
        }
      });

      return () => sub.unsubscribe();
    });

    this.listeners.set(listenerKey, observable);

    return observable;
  }

  public sendMessageSignalR = (objectId: string, itemId: string, hubPath?: string) => {
    const selectedHubPath = this.normalizeHubPath(hubPath);
    const connection = this.getHubConnection(selectedHubPath);

    if (connection?.state === HubConnectionState.Connected) {
      const message = `OBJECT: ${objectId} - ITEM: ${itemId}`;
      console.log(message);
      connection.invoke('Object', objectId, itemId).catch((err) => console.error(err));
      this.messageSentViaSignalR.set(new Date(Date.now()));
    } else {
      console.error(`Cannot send data via signalR (${selectedHubPath}). Connection closed!`);
      console.log('Try to reconnect...');
      this.restartConnection(selectedHubPath);
    }
  };

  public sendValueMessageSignalR = (
    objectId: string,
    itemId: string,
    value: string | number | null | undefined,
    hubPath?: string
  ) => {
    const selectedHubPath = this.normalizeHubPath(hubPath);
    const connection = this.getHubConnection(selectedHubPath);

    if (connection?.state === HubConnectionState.Connected) {
      const normalizedValue = value == null ? '' : `${value}`;
      const message = `OBJECT: ${objectId} - ITEM: ${itemId} - VALUE: ${normalizedValue}`;
      console.log(message);
      connection.invoke('ValueObject', objectId, itemId, normalizedValue).catch((err) => console.error(err));
      this.messageSentViaSignalR.set(new Date(Date.now()));
    } else {
      console.error(`Cannot send data via signalR (${selectedHubPath}). Connection closed!`);
      console.log('Try to reconnect...');
      this.restartConnection(selectedHubPath);
    }
  };
}

export function provideSignalRService(options: {
  factoryClientId?: () => string;
  factoryAuthToken?: () => () => string;
  factorySignalRHubPath?: () => string;
}) {
  return makeEnvironmentProviders([
    {
      provide: CLIENT_ID,
      multi: false,
      useFactory: options.factoryClientId ?? (() => ''),
    },
    {
      provide: AUTH_TOKEN_GETTER,
      multi: false,
      useFactory: options.factoryAuthToken ?? (() => () => ''),
    },
    {
      provide: SIGNALR_HUB_PATH,
      multi: false,
      useFactory: options.factorySignalRHubPath ?? (() => '/MessageHub'),
    },
    SignalRService,
  ]);
}
